import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import SydButton from '../../../../../../commonDesign/Button'
import Icon from '../../../../../../atoms/Icon'
import TestDiffModal from './TestDiffModal'

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid var(--color-ai-200)',
    background: 'linear-gradient(135deg, var(--color-ai-050) 0%, white 100%)'
  },
  removeRoot: {
    border: '1px solid var(--color-danger-200)',
    background: 'linear-gradient(135deg, var(--color-danger-050) 0%, white 100%)'
  },
  titleRow: {
    color: 'var(--color-ai-700)'
  },
  removeTitleRow: {
    color: 'var(--color-danger-700)'
  },
  description: {
    color: 'var(--color-neutral-600)'
  },
  divider: {
    height: '1px',
    background: 'var(--color-ai-200)'
  },
  dismissed: {
    border: '1px dashed var(--color-neutral-300)',
    background: 'var(--color-neutral-050)'
  },
  dismissedLabel: {
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic'
  },
  applied: {
    border: '1px solid var(--color-success-300)',
    background: 'var(--color-success-050)'
  },
  appliedLabel: {
    color: 'var(--color-success-600)',
    fontWeight: 600
  },
  badge: {
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase'
  }
}))

function ProposedTestCard ({ testArgs, onApplyTest, existingTests, autoAccept }) {
  const classes = useStyles()
  const [state, setState] = useState('pending')
  const [showDiff, setShowDiff] = useState(false)
  const isRemove = !!testArgs.remove
  const autoApplied = useRef(false)

  const existingTest = (existingTests || []).find(t => t.name === testArgs.name) || null
  const isUpdate = !isRemove && !!existingTest

  useEffect(() => {
    if (autoAccept && state === 'pending' && onApplyTest && !autoApplied.current) {
      autoApplied.current = true
      onApplyTest(testArgs)
      setState('auto-applied')
    }
  }, [autoAccept, state, onApplyTest, testArgs])

  if (state === 'auto-applied') {
    const autoLabel = isRemove
      ? `Test auto-removed: ${testArgs.name}`
      : isUpdate
        ? `Test auto-updated: ${testArgs.name}`
        : `Test auto-applied: ${testArgs.name}`
    return (
      <div className={`${classes.root} ${classes.applied} flex flex-col gap-2 radius-lg shadow-sm my-3 p-3`}>
        <div className='flex items-center gap-2 p-1'>
          <Icon name='check' customSize='16px' />
          <span className={`${classes.appliedLabel} font-size-xs`}>{autoLabel}</span>
        </div>
      </div>
    )
  }

  if (state === 'dismissed') {
    return (
      <div className={`${classes.root} ${classes.dismissed} flex flex-col gap-2 radius-lg shadow-sm my-3 p-3`}>
        <div className={`${classes.dismissedLabel} font-size-xs p-1`}>
          Test proposal dismissed: {testArgs.name}
        </div>
      </div>
    )
  }

  if (state === 'applied') {
    return (
      <div className={`${classes.root} ${classes.applied} flex flex-col gap-2 radius-lg shadow-sm my-3 p-3`}>
        <div className='flex items-center gap-2 p-1'>
          <Icon name='check' customSize='16px' />
          <span className={`${classes.appliedLabel} font-size-xs`}>
            {isRemove ? `Test removed: ${testArgs.name}` : `Test applied: ${testArgs.name}`}
          </span>
        </div>
      </div>
    )
  }

  const handleApplyFromModal = (args) => {
    if (onApplyTest) onApplyTest(args)
    setState('applied')
    setShowDiff(false)
  }

  const strategy = testArgs.mocks?.strategy
  const mockCount = testArgs.mocks?.responses?.length || 0

  const rootClass = isRemove ? classes.removeRoot : classes.root
  const titleClass = isRemove ? classes.removeTitleRow : classes.titleRow

  let actionLabel = 'Review & Add Test'
  if (isRemove) actionLabel = 'Review & Remove'
  else if (isUpdate) actionLabel = 'Review Changes'

  return (
    <>
      <div className={`${rootClass} flex flex-col gap-2 radius-lg shadow-sm my-3 p-3`}>
        <div className='flex items-center justify-between gap-2'>
          <div className={`${titleClass} flex items-center gap-2`}>
            <Icon name={isRemove ? 'remove' : isUpdate ? 'edit' : 'add'} customSize='18px' />
            <span className='font-size-sm font-weight-semibold'>
              {isRemove ? 'Remove Test' : isUpdate ? 'Update Test' : 'Proposed Test'}
            </span>
          </div>
          <div className='flex gap-1'>
            {strategy && (
              <span
                className={classes.badge}
                style={{
                  backgroundColor: strategy === 'throwOnMiss' ? 'var(--color-primary-050)' : 'var(--color-warning-050)',
                  color: strategy === 'throwOnMiss' ? 'var(--color-primary-700)' : 'var(--color-warning-700)'
                }}
              >
                {strategy === 'throwOnMiss' ? 'unit' : 'integration'}
              </span>
            )}
            {mockCount > 0 && (
              <span
                className={classes.badge}
                style={{ backgroundColor: 'var(--color-neutral-100)', color: 'var(--color-neutral-600)' }}
              >
                {mockCount} mock{mockCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        <div className='font-size-sm font-weight-medium'>{testArgs.name}</div>

        {testArgs.description && (
          <div className={`${classes.description} font-size-xs`}>{testArgs.description}</div>
        )}

        <div className={classes.divider} />
        <div className='flex items-center justify-between gap-2'>
          <SydButton size='xs' variant='outline' onClick={() => setState('dismissed')}>
            Dismiss
          </SydButton>
          <SydButton
            size='xs'
            variant={isRemove ? 'outline' : 'ai'}
            onClick={() => setShowDiff(true)}
          >
            {actionLabel}
          </SydButton>
        </div>
      </div>

      {showDiff && (
        <TestDiffModal
          testArgs={testArgs}
          existingTest={existingTest}
          onApply={handleApplyFromModal}
          onDismiss={() => setShowDiff(false)}
        />
      )}
    </>
  )
}

ProposedTestCard.propTypes = {
  testArgs: PropTypes.shape({
    name: PropTypes.string.isRequired,
    variables: PropTypes.object,
    mocks: PropTypes.object,
    assertion: PropTypes.string,
    description: PropTypes.string,
    remove: PropTypes.bool
  }).isRequired,
  onApplyTest: PropTypes.func,
  existingTests: PropTypes.array,
  autoAccept: PropTypes.bool
}

export default ProposedTestCard
